import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Briefcase } from 'lucide-react';
import careerPhoto from '../../assets/forCv.jpg';

const Career = () => {
  const { t } = useTranslation();

  const cards = [
    { id: 0, num: '01', text: t('careerP1') },
    { id: 1, num: '02', text: t('careerP2') },
    { id: 2, num: '03', text: t('careerP3') },
    { id: 3, num: '04', text: t('careerP4') },
    { id: 4, num: '05', text: t('careerP5') },
  ];

  const Card = ({ card, delay }) => (
    <motion.div
      className="cr-card"
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay }}
    >
      <span className="cr-num">{card.num}</span>
      <p className="cr-text">{card.text}</p>
    </motion.div>
  );

  return (
    <section id="career">
      <style>{`
        .career-section {
          background: linear-gradient(135deg, #0f2350 0%, #1b3f82 50%, #2563eb 100%);
          border-top: 1px solid rgba(71,146,244,0.30);
          padding: 5rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 3rem;
          box-sizing: border-box;
          overflow: hidden;
        }
        .career-header {
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
        }
        .career-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(71,146,244,0.18);
          border: 1px solid rgba(71,146,244,0.45);
          border-radius: 50px;
          padding: 0.35rem 1rem;
          font-size: 0.8rem;
          font-weight: 700;
          color: #a8c8ff;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .career-title {
          font-size: clamp(1.8rem, 3vw, 2.6rem);
          font-weight: 700;
          color: #ffffff;
          margin: 0;
          letter-spacing: -0.02em;
        }

        /* Main 3-col grid */
        .cr-grid {
          max-width: 1200px;
          width: 100%;
          display: grid;
          grid-template-columns: 1fr 340px 1fr;
          grid-template-rows: 1fr auto;
          gap: 1.25rem;
          align-items: stretch;
        }
        .cr-left  { grid-column: 1; grid-row: 1; display: flex; flex-direction: column; gap: 1.25rem; align-items: stretch; }
        .cr-center { grid-column: 2; grid-row: 1; display: flex; flex-direction: column; }
        .cr-right { grid-column: 3; grid-row: 1; display: flex; flex-direction: column; gap: 1.25rem; align-items: stretch; }
        .cr-bottom { grid-column: 1 / 4; grid-row: 2; display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }

        .cr-photo {
          width: 100%;
          border-radius: 20px;
          object-fit: cover;
          flex: 1;
          min-height: 300px;
          display: block;
          border: 1px solid rgba(255,255,255,0.22);
          box-shadow: 0 12px 40px rgba(0,0,0,0.35);
        }

        /* Card styles */
        .cr-card {
          background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 14px;
          padding: 1.25rem 1.4rem;
          display: flex;
          flex-direction: column;
          gap: 0.6rem;
          flex: 1;
          transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
        }
        .cr-card:hover {
          background: rgba(255,255,255,0.11);
          border-color: rgba(71,146,244,0.45);
          box-shadow: 0 4px 20px rgba(71,146,244,0.13);
        }
        .cr-num {
          font-size: 1.4rem;
          font-weight: 800;
          color: rgba(71,146,244,0.45);
          line-height: 1;
        }
        .cr-text {
          font-size: 0.9rem;
          color: #d0e4ff;
          line-height: 1.75;
          margin: 0;
        }

        /* CTA */
        .cr-cta {
          background: linear-gradient(135deg, #4792f4 0%, #2563eb 100%);
          border-radius: 14px;
          padding: 1.5rem 1.75rem;
          box-shadow: 0 8px 24px rgba(71,146,244,0.4);
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .cr-cta-text {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.95);
          line-height: 1.7;
          margin: 0;
        }
        .cr-email {
          display: inline-block;
          color: #fff;
          font-weight: 700;
          font-size: 1rem;
          text-decoration: none;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 8px;
          padding: 0.45rem 1.1rem;
          transition: background 0.2s;
          align-self: flex-start;
        }
        .cr-email:hover { background: rgba(255,255,255,0.25); }

        /* Mobile */
        @media (max-width: 860px) {
          .cr-grid {
            grid-template-columns: 1fr;
            grid-template-rows: auto;
          }
          .cr-center {
            grid-column: 1;
            grid-row: 1;
            position: static;
          }
          .cr-left  { grid-column: 1; grid-row: 2; }
          .cr-right { grid-column: 1; grid-row: 3; }
          .cr-bottom { grid-column: 1; grid-row: 4; grid-template-columns: 1fr; }
          .cr-photo { height: 240px; }
        }
      `}</style>

      <div className="career-section">
        <motion.div
          className="career-header"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="career-badge">
            <Briefcase size={13} />
            {t('careerBadge')}
          </div>
          <h2 className="career-title">{t('careerTitle')}</h2>
        </motion.div>

        <div className="cr-grid">
          {/* Left column — cards 1,2 */}
          <div className="cr-left">
            <Card card={cards[0]} delay={0.1} />
            <Card card={cards[1]} delay={0.2} />
          </div>

          {/* Center — image */}
          <motion.div
            className="cr-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <img className="cr-photo" src={careerPhoto} alt="Career at GeoGlobalGate" />
          </motion.div>

          {/* Right column — cards 3,4 */}
          <div className="cr-right">
            <Card card={cards[2]} delay={0.1} />
            <Card card={cards[3]} delay={0.2} />
          </div>

          {/* Bottom row — card 5 + CTA */}
          <div className="cr-bottom">
            <Card card={cards[4]} delay={0.25} />
            <motion.div
              className="cr-cta"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <p className="cr-cta-text">{t('careerCta')}</p>
              <a className="cr-email" href="mailto:t1@geoglobalgate.com">t1@geoglobalgate.com</a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Career;
